import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizQuestions, quizProfiles } from '../../mocks/quiz-autoconhecimento';
import QuizHero from './components/QuizHero';
import QuizQuestions from './components/QuizQuestions';
import QuizResult from './components/QuizResult';
import SEOHead from '../../components/feature/SEOHead';

type QuizStage = 'intro' | 'questions' | 'result';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const quizSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Quiz de Autoconhecimento | Carta do Eu Futuro',
    url: `${SITE_URL}/quiz`,
    inLanguage: 'pt-BR',
    description: 'Descubra seu perfil de desenvolvimento pessoal com o quiz gratuito de autoconhecimento do Carta do Eu Futuro.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Quiz', item: `${SITE_URL}/quiz` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Quiz',
    name: 'Quiz de Autoconhecimento e Desenvolvimento Pessoal',
    description: 'Descubra seu perfil de desenvolvimento pessoal e receba recomendações personalizadas de exercícios de PNL e práticas de gratidão.',
    about: {
      '@type': 'Thing',
      name: 'Desenvolvimento Pessoal',
    },
    educationalLevel: 'Iniciante a Avançado',
    inLanguage: 'pt-BR',
    isAccessibleForFree: true,
    author: {
      '@type': 'Person',
      name: 'Bia Holmes',
      url: `${SITE_URL}/sobre`,
    },
  },
];

const QuizPage = () => {
  const [stage, setStage] = useState<QuizStage>('intro');
  const [resultProfile, setResultProfile] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleStart = useCallback(() => {
    setStage('questions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleComplete = useCallback((answers: Record<number, string>) => {
    const counts: Record<string, number> = {};
    Object.values(answers).forEach((profile) => {
      counts[profile] = (counts[profile] || 0) + 1;
    });
    const winner = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
    setResultProfile(winner);
    setStage('result');
  }, []);

  const handleRestart = useCallback(() => {
    setResultProfile(null);
    setStage('intro');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-luxury-black">
      <SEOHead
        title="Quiz de Autoconhecimento Gratuito | Descubra Seu Perfil — Carta do Eu Futuro"
        description="Faça o quiz gratuito de autoconhecimento e descubra seu perfil de desenvolvimento pessoal. Receba recomendações personalizadas de PNL e gratidão."
        keywords="quiz autoconhecimento, perfil desenvolvimento pessoal, PNL gratuito, teste pessoal"
        canonical="/quiz"
        ogImage="https://public.readdy.ai/ai/img_res/3928c6270938664ba9c2ecceecebf008.jpg"
        ogType="website"
        schema={quizSchema}
      />
      {/* Navbar fixa */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-luxury-dark/90 backdrop-blur-xl border-b border-gold-500/10">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="flex items-center justify-between h-24">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <img
                src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
                alt="Carta do Eu Futuro"
                className="w-16 h-16 object-contain group-hover:scale-110 group-hover:drop-shadow-[0_0_14px_rgba(201,164,64,0.55)] transition-all duration-300"
              />
              <span className="font-['Playfair_Display'] text-lg font-semibold text-white hidden sm:block">
                Carta do Eu Futuro
              </span>
            </a>
            <div className="flex items-center gap-6">
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
                className="font-['Montserrat'] text-xs uppercase tracking-wider text-white/50 hover:text-gold-400 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                Início
              </a>
              <a
                href="/sobre"
                onClick={(e) => { e.preventDefault(); navigate('/sobre'); }}
                className="font-['Montserrat'] text-xs uppercase tracking-wider text-white/50 hover:text-gold-400 transition-colors duration-300 cursor-pointer whitespace-nowrap hidden md:block"
              >
                Sobre
              </a>
              <a
                href="/comunidade"
                onClick={(e) => { e.preventDefault(); navigate('/comunidade'); }}
                className="font-['Montserrat'] text-xs uppercase tracking-wider text-white/50 hover:text-gold-400 transition-colors duration-300 cursor-pointer whitespace-nowrap hidden md:block"
              >
                Comunidade
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Conteúdo */}
      {stage === 'intro' && <QuizHero onStart={handleStart} />}
      {stage === 'questions' && (
        <QuizQuestions questions={quizQuestions} onComplete={handleComplete} />
      )}
      {stage === 'result' && resultProfile && (
        <QuizResult profile={quizProfiles[resultProfile]} onRestart={handleRestart} />
      )}

      {/* Footer simples */}
      <footer className="bg-luxury-champagne/40 py-10 relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent"></div>
        <div className="container mx-auto px-6 text-center">
          <p className="font-['Montserrat'] text-xs text-luxury-charcoal/30 tracking-wide">
            © 2025 Carta do Eu Futuro. Todos os direitos reservados.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="font-['Montserrat'] text-xs text-luxury-charcoal/30 hover:text-gold-600 transition-colors cursor-pointer tracking-wide mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
};

export default QuizPage;
